import React from 'react';
import '../../styles/enterprise.css';
import { Coins } from 'lucide-react';
import { TokenChainLogo } from './CryptoLogo';

/**
 * Real token logo + corner chain logo (CMC style) followed by symbol/chain text.
 *
 * <AssetBadge symbol="USDC" chain="Base" />
 *
 * Optional logoUrl / chainLogoUrl override the CDN lookup with a DB-provided
 * image. Falls back to a colored letter glyph when no logo can be loaded.
 *
 * With no symbol it renders a pending state instead: a dashed placeholder of
 * the same diameter plus the same two-line text block, so a row whose asset is
 * not set keeps the logo column and the row height of the rows around it.
 *
 * pendingLabel / pendingHint override the copy. The defaults stay neutral
 * because callers differ — an invoice has no asset until the payer picks one at
 * checkout, whereas a conversion row missing one would mean something else.
 */
const AssetBadge = ({
  symbol,
  chain,
  logoUrl,
  chainLogoUrl,
  size = 28,
  pendingLabel = 'Not selected',
  pendingHint = '',
}) => {
  const sym = String(symbol ?? '').trim().toUpperCase();

  if (!sym) {
    return (
      <span className="asset-badge" title={pendingHint || pendingLabel}>
        <span
          className="asset-badge__pending-glyph"
          style={{ width: size, height: size }}
          aria-hidden="true"
        >
          <Coins size={Math.round(size * 0.5)} strokeWidth={1.75} />
        </span>
        <span className="asset-badge__text">
          <span className="asset-badge__sym asset-badge__sym--pending">{pendingLabel}</span>
          {pendingHint ? <span className="asset-badge__chain">{pendingHint}</span> : null}
        </span>
      </span>
    );
  }

  return (
    <span className="asset-badge">
      <TokenChainLogo
        symbol={sym}
        tokenLogoUrl={logoUrl}
        chain={chain}
        chainLogoUrl={chainLogoUrl}
        size={size}
      />
      <span className="asset-badge__text">
        <span className="asset-badge__sym">{sym}</span>
        {chain ? <span className="asset-badge__chain">{chain}</span> : null}
      </span>
    </span>
  );
};

export default AssetBadge;
